/**
 * Orthogonal Route Calculator
 *
 * FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/orthogonalRouter.js
 *
 * Calculates orthogonal (right-angle) routing paths with obstacle avoidance
 * Updated: Turn penalty reduced to 0.3 for shorter paths
 */

import { buildObstacleGrid, findPath, smoothPath, gridToCanvasCoords } from './pathfinding.js';

/**
 * Calculate orthogonal route between two points with obstacle avoidance
 *
 * @param {Object} fromPos - Start position {x, y, type}
 * @param {Object} toPos - End position {x, y, type}
 * @param {Array} componentNodes - Array of ComponentNode objects for obstacle detection
 * @param {Object} options - Routing options
 * @returns {Array} Array of waypoint objects {x, y}
 */
export function calculateOrthogonalRoute(fromPos, toPos, componentNodes, options = {}) {
  const {
    exitOffset = 30,      // Minimum horizontal distance before first turn
    entryOffset = 30,     // Minimum horizontal distance before entering target
    gridSize = 10,        // Grid cell size for pathfinding
    canvasWidth = 4000,   // Canvas bounds (doubled to allow negative coords)
    canvasHeight = 4000,
    offsetX = 2000,       // Offset to convert negative coords to positive
    offsetY = 2000,
    existingWirePaths = [], // Previously routed wire paths to treat as obstacles
  } = options;

  // Start pathfinding one grid cell away from slots to avoid landing on obstacles
  // Output slots are on the right edge, so start one cell to the right
  // Input slots are on the left edge, so end one cell to the left
  const adjustedStart = {
    x: fromPos.x + gridSize + offsetX,  // One grid cell right of output slot
    y: fromPos.y + offsetY,
  };

  const adjustedEnd = {
    x: toPos.x - gridSize + offsetX,    // One grid cell left of input slot
    y: toPos.y + offsetY,
  };

  // Build obstacle grid from visible components AND existing wire paths
  const grid = buildObstacleGrid(
    Object.values(componentNodes),
    gridSize,
    canvasWidth,
    canvasHeight,
    offsetX,
    offsetY,
    existingWirePaths,  // Pass existing wires as obstacles
  );

  // Find path using Lee's algorithm
  const gridPath = findPath(adjustedStart, adjustedEnd, grid, gridSize);

  if (!gridPath) {
    console.warn('[OrthogonalRouter] No path found, using fallback routing');
    return createFallbackRoute(fromPos, toPos, exitOffset, entryOffset);
  }

  // Smooth the path (remove redundant waypoints)
  const smoothedPath = smoothPath(gridPath);

  // Convert grid coordinates to canvas coordinates
  let waypoints = gridToCanvasCoords(smoothedPath, gridSize);

  // Un-offset waypoints back to canvas coordinates
  waypoints = waypoints.map((wp) => ({
    x: wp.x - offsetX,
    y: wp.y - offsetY,
  }));

  // Add actual slot positions at start and end
  // Check if first waypoint is horizontally aligned with slot (same Y)
  if (waypoints.length > 0 && Math.abs(waypoints[0].y - fromPos.y) < 0.1) {
    // First waypoint is on same horizontal line, just prepend slot
    waypoints.unshift(fromPos);
  } else {
    // First waypoint is on different line, add intermediate point
    waypoints.unshift({ x: waypoints[0].x, y: fromPos.y }, fromPos);
  }

  // Check if last waypoint is horizontally aligned with slot (same Y)
  const lastIdx = waypoints.length - 1;
  if (Math.abs(waypoints[lastIdx].y - toPos.y) < 0.1) {
    // Last waypoint is on same horizontal line, just append slot
    waypoints.push(toPos);
  } else {
    // Last waypoint is on different line, add intermediate point
    waypoints.push({ x: waypoints[lastIdx].x, y: toPos.y }, toPos);
  }

  // Remove duplicate consecutive points
  waypoints = removeDuplicates(waypoints);

  // Ensure all movements are orthogonal (no diagonals)
  waypoints = enforceOrthogonal(waypoints);

  return waypoints;
}

/**
 * Create fallback route when A* fails (simple 5-segment orthogonal path)
 */
function createFallbackRoute(fromPos, toPos, exitOffset, entryOffset) {

  const waypoints = [
    fromPos,
    { x: fromPos.x + exitOffset, y: fromPos.y },
    { x: fromPos.x + exitOffset, y: toPos.y },
    { x: toPos.x - entryOffset, y: toPos.y },
    toPos,
  ];

  return removeDuplicates(waypoints);
}

/**
 * Remove duplicate consecutive waypoints
 */
function removeDuplicates(waypoints) {
  if (!waypoints || waypoints.length <= 1) {return waypoints;}

  const unique = [waypoints[0]];

  for (let i = 1; i < waypoints.length; i++) {
    const prev = waypoints[i - 1];
    const current = waypoints[i];

    // Only add if different from previous point
    if (Math.abs(current.x - prev.x) > 0.1 || Math.abs(current.y - prev.y) > 0.1) {
      unique.push(current);
    }
  }

  return unique;
}

/**
 * Enforce orthogonal (right-angle) movements only
 * Converts any diagonal segments into two orthogonal segments
 */
function enforceOrthogonal(waypoints) {
  if (!waypoints || waypoints.length <= 1) {return waypoints;}

  const orthogonal = [waypoints[0]];

  for (let i = 1; i < waypoints.length; i++) {
    const prev = waypoints[i - 1];
    const current = waypoints[i];

    const dx = Math.abs(current.x - prev.x);
    const dy = Math.abs(current.y - prev.y);

    // If both dx and dy are non-zero, it's diagonal - split into two orthogonal moves
    if (dx > 0.1 && dy > 0.1) {
      // Insert intermediate point (horizontal first, then vertical)
      orthogonal.push({ x: current.x, y: prev.y });
    }

    orthogonal.push(current);
  }

  return orthogonal;
}

/**
 * Calculate rounded corner points for smooth 90° turns
 *
 * @param {Array} waypoints - Array of waypoint objects {x, y}
 * @param {Number} cornerRadius - Radius of corner curves
 * @returns {Array} Array of points for Konva line rendering
 */
export function calculateRoundedCorners(waypoints, cornerRadius = 10) {
  if (!waypoints || waypoints.length < 2) {return [];}

  const points = [];

  // Start point (no curve)
  points.push(waypoints[0].x, waypoints[0].y);

  // Process each corner
  for (let i = 1; i < waypoints.length - 1; i++) {
    const prev = waypoints[i - 1];
    const current = waypoints[i];
    const next = waypoints[i + 1];

    // Calculate direction vectors
    const dx1 = current.x - prev.x;
    const dy1 = current.y - prev.y;
    const dx2 = next.x - current.x;
    const dy2 = next.y - current.y;

    // Normalize to get unit vectors
    const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

    if (len1 === 0 || len2 === 0) {continue;}

    const ux1 = dx1 / len1;
    const uy1 = dy1 / len1;
    const ux2 = dx2 / len2;
    const uy2 = dy2 / len2;

    // Calculate the actual corner radius (limited by segment length)
    const maxRadius = Math.min(cornerRadius, len1 / 2, len2 / 2);

    // Points where the curve starts and ends
    const cornerStart = {
      x: current.x - ux1 * maxRadius,
      y: current.y - uy1 * maxRadius,
    };

    const cornerEnd = {
      x: current.x + ux2 * maxRadius,
      y: current.y + uy2 * maxRadius,
    };

    // Add straight line up to corner
    points.push(cornerStart.x, cornerStart.y);

    // Add quadratic curve through corner
    // Control point is at the corner itself
    points.push(current.x, current.y);

    // Curve ends at cornerEnd (next segment will continue from here)
    points.push(cornerEnd.x, cornerEnd.y);
  }

  // End point (no curve)
  const lastPoint = waypoints[waypoints.length - 1];
  points.push(lastPoint.x, lastPoint.y);

  return points;
}

/**
 * Check if direct path is possible (no obstacles)
 */
export function hasDirectPath(fromPos, toPos, componentNodes, gridSize = 10) {
  // Simple check: try to find path, see if it's straight
  const grid = buildObstacleGrid(
    Object.values(componentNodes),
    gridSize,
    2000,
    2000,
  );

  const path = findPath(fromPos, toPos, grid, gridSize);

  if (!path) {return false;}

  // Check if path is a straight line (only 2-3 points)
  return path.length <= 3;
}

export default {
  calculateOrthogonalRoute,
  calculateRoundedCorners,
  hasDirectPath,
};
